// String filter operations
export type StringOperator =
  "equals" | "notEquals" | "contains" | "startsWith" | "in";
export type StringFilterSpec = {
  type: "string";
  operator: "equals" | "notEquals" | "contains" | "startsWith";
  value: string | null;
};

export type StringInFilterSpec = {
  type: "string";
  operator: "in";
  value: (string | null)[];
};

// Boolean filter operations
export type BooleanOperator = "equals";
export interface BooleanFilterSpec {
  type: "boolean";
  operator: BooleanOperator;
  value: boolean | null;
}

// Date filter operations
export type DateOperator = "equals" | "notEquals" | "after" | "before";
export type DateFilterSpec = {
  type: "date";
  operator: DateOperator;
  value: Date | null;
};

export type DateRangeFilterSpec = {
  type: "date";
  operator: "range";
  value: [Date, Date] | null;
};

export const RELATIVE_DATE_UNITS = [
  "today",
  "yesterday",
  "lastWeek",
  "last2Weeks",
  "lastMonth",
  "last3Months",
  "last6Months",
  "lastYear",
] as const;

export type RelativeDateUnit = (typeof RELATIVE_DATE_UNITS)[number];

export const RELATIVE_DATE_LABELS: Record<RelativeDateUnit, string> = {
  today: "Today",
  yesterday: "Yesterday",
  lastWeek: "Last week",
  last2Weeks: "Last 2 weeks",
  lastMonth: "Last month",
  last3Months: "Last 3 months",
  last6Months: "Last 6 months",
  lastYear: "Last year",
};

export interface RelativeDateFilterSpec {
  type: "relativeDate";
  operator: "after";
  value: RelativeDateUnit | null;
}

// Number filter operations
export type NumberOperator =
  | "equals"
  | "notEquals"
  | "lessThan"
  | "greaterThan"
  | "lessThanEqualTo"
  | "greaterThanEqualTo";

export interface NumberFilterSpec {
  type: "number";
  operator: NumberOperator;
  value: number | null;
}

export type NumberRangeFilterSpec = {
  type: "number";
  operator: "range";
  value: [number, number];
};

export type ArrayOperator = "any" | "all";
export interface ArrayFilterSpec<
  T =
    | StringFilterSpec
    | StringInFilterSpec
    | BooleanFilterSpec
    | DateFilterSpec
    | DateRangeFilterSpec
    | RelativeDateFilterSpec
    | NumberFilterSpec
    | NumberRangeFilterSpec,
> {
  type: "array";
  operator: ArrayOperator;
  itemSpec: T;
}

export type ArrayLengthOperator = "length";
export interface ArrayLengthFilterSpec {
  type: "array";
  operator: ArrayLengthOperator;
  itemSpec: NumberFilterSpec | NumberRangeFilterSpec;
}

export interface QuickFilterSpec {
  type: "quick";
  term: string | null;
  textFields: string[];
  idArrayFields: string[];
}

export type FilterSpec =
  | StringFilterSpec
  | StringInFilterSpec
  | BooleanFilterSpec
  | DateFilterSpec
  | DateRangeFilterSpec
  | RelativeDateFilterSpec
  | NumberFilterSpec
  | NumberRangeFilterSpec
  | ArrayFilterSpec
  | ArrayLengthFilterSpec
  | QuickFilterSpec;

// Filter spec creator functions

// String filter creator for equals/notEquals/contains/startsWith operators
export const createStringFilter = (
  operator: "equals" | "notEquals" | "contains" | "startsWith",
  value: string | null,
): StringFilterSpec => ({
  type: "string",
  operator,
  value,
});

// String filter creator specifically for 'in' operator
export const createStringInFilter = (
  value: (string | null)[],
): StringInFilterSpec => ({
  type: "string",
  operator: "in",
  value,
});

/** Resolves any string filter shape to its matching subset of `options`. */
export const stringFilterOptions = (
  spec: FilterSpec | undefined,
  options: string[],
): string[] => {
  if (spec?.type !== "string") {
    return options;
  }
  if (spec.operator === "in") {
    return spec.value.filter((v): v is string => v !== null);
  }
  if (spec.operator === "equals" && spec.value === null) {
    return [];
  }
  return options;
};

// Boolean filter creator
export const createBooleanFilter = (
  operator: BooleanOperator,
  value: boolean | null,
): BooleanFilterSpec => ({
  type: "boolean",
  operator,
  value,
});

// Number filter creator
export const createNumberFilter = (
  operator: NumberOperator,
  value: number | null,
): NumberFilterSpec => ({
  type: "number",
  operator,
  value,
});

// Number range filter creator
export const createNumberRangeFilter = (
  value: [from: number, to: number],
): NumberRangeFilterSpec => ({
  type: "number",
  operator: "range",
  value,
});

// Date filter creator for before/after operators
export const createDateFilter = (
  operator: DateOperator,
  value: Date | null,
): DateFilterSpec => ({
  type: "date",
  operator,
  value,
});

// Date filter creator specifically for range operator
export const createDateRangeFilter = (
  value: [Date, Date] | null,
): DateRangeFilterSpec => ({
  type: "date",
  operator: "range",
  value,
});

// Relative date filter creator
export const createRelativeDateFilter = (
  operator: "after",
  value: RelativeDateUnit | null,
): RelativeDateFilterSpec => ({
  type: "relativeDate",
  operator,
  value,
});

// Array filter creator
export function createArrayFilter<
  T =
    | StringFilterSpec
    | StringInFilterSpec
    | BooleanFilterSpec
    | DateFilterSpec
    | DateRangeFilterSpec
    | RelativeDateFilterSpec
    | NumberFilterSpec,
>(operator: ArrayOperator, itemSpec: T): ArrayFilterSpec<T>;
export function createArrayFilter(
  operator: ArrayLengthOperator,
  itemSpec: NumberFilterSpec | NumberRangeFilterSpec,
): ArrayLengthFilterSpec;
export function createArrayFilter(
  operator: ArrayOperator | ArrayLengthOperator,
  itemSpec: unknown,
) {
  return {
    type: "array",
    operator,
    itemSpec,
  };
}

export const createQuickFilter = (
  term: string | null,
  textFields: string[],
  idArrayFields: string[] = [],
): QuickFilterSpec => ({
  type: "quick",
  term,
  textFields,
  idArrayFields,
});
